/* eslint-env browser */

import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import copyToClipboard from 'copy-to-clipboard';

import {
  isSocketBonus,
  itemToSimc,
  isTitanDiscBelt,
  getTitanDiscInfo,
} from 'shared/items';
import {
  getGemInfo,
} from 'shared/gems';
import { getEnchants } from 'shared/enchants';
import { tagByKey } from 'shared/itemTags';
import GameData from 'shared/gameData';

import config from 'frontend/config';

import Icon from './Icon';
import WowIcon, { sizeMap, itemBadge } from './WowIcon';

const MIN_SIZE_FOR_BADGE = 20;

const WowItemIcon = ({
  item,
  classId,
  specId,
  setPieceIds,
  slot,
  showGems,
  hideBadge,
  hideCraftingQuality,
  ...props
}) => {
  const [showCopyIcon, setShowCopyIcon] = React.useState(false);

  const sizePx = sizeMap[props.size] || props.size;
  if (!item) {
    const emptySlotIcons = {
      head: 'Head',
      neck: 'Neck',
      shoulder: 'Shoulder',
      back: 'Chest',
      chest: 'Chest',
      shirt: 'Shirt',
      tabard: 'Tabard',
      wrist: 'Wrists',
      hands: 'Hands',
      waist: 'Waist',
      legs: 'Legs',
      feet: 'Feet',
      finger: 'Finger',
      finger1: 'Finger',
      finger2: 'Finger',
      trinket: 'Trinket',
      trinket1: 'Trinket',
      trinket2: 'Trinket',
      main_hand: 'MainHand',
      off_hand: 'SecondaryHand',
    };
    return (
      <img
        src={`/images/paperdoll/UI-PaperDoll-Slot-${emptySlotIcons[slot]}.png`}
        alt={`Empty slot for ${slot}`}
        width={sizePx}
        height={sizePx}
      />
    );
  }

  // bonus lists
  let bonuses;
  if (item.bonus_id) {
    bonuses = item.bonus_id.split('/');
  } else if (item.bonusLists) {
    bonuses = item.bonusLists;
  }
  bonuses = _.map(bonuses, b => parseInt(b, 10));

  // find enchant
  let ench;
  if (item.tooltipParams && item.tooltipParams.enchant) {
    ench = item.tooltipParams.enchant;
  } else if (item.tooltipParams && item.tooltipParams.tinker) {
    ench = item.tooltipParams.tinker;
  } else if (item.enchant_id) {
    ench = item.enchant_id;
  } else if (item.itemEnchant) {
    ench = _.get(_.find(getEnchants(), { key: item.itemEnchant }), 'id');
  }

  // find gems
  let gemStr;
  let gems;
  if (item.gem_id) {
    gems = item.gem_id.split('/');
  } else if (item.tooltipParams) {
    gems = [
      item.tooltipParams.gem0,
      item.tooltipParams.gem1,
      item.tooltipParams.gem2,
    ];
  } else if (item.itemGem0 || item.itemGem1 || item.itemGem2) {
    if (Number.isNaN(Number(item.itemGem0))) {
      gems = [
        _.get(_.find(getGemInfo(), { shortName: item.itemGem0 }), 'itemId'),
        _.get(_.find(getGemInfo(), { shortName: item.itemGem1 }), 'itemId'),
        _.get(_.find(getGemInfo(), { shortName: item.itemGem2 }), 'itemId'),
      ];
    } else {
      gems = [
        item.itemGem0,
        item.itemGem1,
        item.itemGem2,
      ];
    }
  }
  if (gems) {
    const filledGems = _.filter(gems);
    if (filledGems.length > 0) {
      gemStr = filledGems.join(',');
      bonuses = _.reject(bonuses, isSocketBonus);
      bonuses.push(
        _.findKey(GameData.BonusSockets, s => s === filledGems.length),
      );
    }
  }

  let craftedStats;
  if (_.isArray(item.craftedStats)) {
    craftedStats = _.join(item.craftedStats, ':');
  } else if (item.craftedStats) {
    craftedStats = _.join(_.split(item.craftedStats, '/'), ':');
  } else if (item.crafted_stats) {
    craftedStats = _.join(_.split(item.crafted_stats, '/'), ':');
  }

  // spell modifier
  let spellModifier;
  // currently just used by the 11.1.7 Titan DISC Belt
  // I suspect we might see more of this in the future
  if (isTitanDiscBelt(item)) {
    spellModifier = getTitanDiscInfo(item)?.spellModifier;
  }

  const sourceIsChoice = _.some(item.tags, tagKey => tagByKey[tagKey]?.badge);

  const baseIcon = (
    <WowIcon
      icon={item.icon}
      quality={item.quality}
      id={item.id}
      type="item"
      item={item}
      craftingQuality={item.crafting_quality}
      hideCraftingQuality={hideCraftingQuality}
      extra={{
        bonus: _.join(bonuses, ':'),
        ench,
        gems: gemStr,
        setPieces: setPieceIds,
        ilvl: item.itemLevel,
        spec: specId,
        'crafted-stats': craftedStats,
        spellModifier,
      }}
      borderStyle={sourceIsChoice ? 'dotted' : 'solid'}
      {...props}
    />
  );

  let gemOverlay;
  if (showGems && _.size(gems) > 0) {
    const gemSize = 12;
    gemOverlay = (
      <div
        style={{
          position: 'absolute',
          top: -4,
          right: -4,
          height: gemSize,
          opacity: props.dull ? 0.4 : undefined,
          filter: props.dull ? 'grayscale(25%)' : undefined,
          boxShadow: '-2px 4px 4px black',
        }}
      >
        {_.map(gems, (gemId, i) => {
          const gem = _.find(getGemInfo(), { itemId: parseInt(gemId, 10) });
          if (gem) {
            return (
              <WowIcon
                key={`${i}-${gemId}`}
                size={gemSize}
                id={gem.itemId}
                type="item"
                icon={gem.itemIcon}
                pl={1}
                craftingQuality={gem.craftingQuality}
              />
            );
          }
          return null;
        })}
      </div>
    );
  }

  let badgeOverlay;
  const badge = itemBadge(item);
  if (!hideBadge && badge && sizePx > MIN_SIZE_FOR_BADGE) {
    badgeOverlay = (
      <div
        style={{
          overflow: 'hidden',
          position: 'absolute',
          top: 0,
          left: 0,
          cursor: 'pointer',
          opacity: props.dull ? 0.4 : undefined,
          filter: props.dull ? 'grayscale(25%)' : undefined,
        }}
        title={badge.title}
      >
        <div
          style={{
            background: badge.color,
            textAlign: 'center',
            fontFamily: 'Tahoma',
            fontSize: sizePx / 4,
            position: 'relative',
            padding: 1,
          }}
        >
          {badge.text}
        </div>
      </div>
    );
  }

  let copyIconOverlay;
  if (showCopyIcon) {
    copyIconOverlay = (
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <Icon name="copy" shadow size={sizePx * 0.75} color={config.colors.blue} />
      </div>
    );
  }

  let spellIconOverlay;
  if (spellModifier && sizePx > 20) {
    spellIconOverlay = (
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
        }}
      >
        <WowIcon
          id={spellModifier}
          type="spell"
          size={12}
          border={`1px solid ${config.colors.blue}`}
          extra={{
            ilvl: item.itemLevel,
          }}
        />
      </div>
    );
  }


  const itemIcon = (
    // eslint-disable-next-line
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        lineHeight: 1,
        position: 'relative',
        height: sizePx,
      }}
      onClick={e => {
        if (e.shiftKey) {
          e.preventDefault();
          e.stopPropagation();
          document.getSelection().removeAllRanges();
          copyToClipboard(itemToSimc(item));

          // briefly show an copy icon
          setShowCopyIcon(true);
          setTimeout(() => {
            setShowCopyIcon(false);
          }, 500);
          return;
        }
        if (props.onClick) {
          props.onClick(e);
        }
      }}
    >
      {baseIcon}
      {gemOverlay}
      {badgeOverlay}
      {copyIconOverlay}
      {spellIconOverlay}
    </div>
  );

  return itemIcon;
};

WowItemIcon.propTypes = {
  // eslint-disable-next-line
  item: PropTypes.object,
  classId: PropTypes.number,
  specId: PropTypes.number,
  // eslint-disable-next-line
  setPieceIds: PropTypes.array,
  slot: PropTypes.string,
  showGems: PropTypes.bool,
  hideBadge: PropTypes.bool,
  hideCraftingQuality: PropTypes.bool,
  size: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  dull: PropTypes.bool,
  onClick: PropTypes.func,
};

WowItemIcon.defaultProps = {
  item: null,
  classId: null,
  specId: null,
  setPieceIds: null,
  slot: null,
  showGems: false,
  hideBadge: false,
  hideCraftingQuality: false,
  size: 'medium',
  dull: false,
  onClick: _.noop,
};

export default WowItemIcon;
